import React, { forwardRef, useCallback } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import CommentContainer from 'components/Comment/CommentContainer';
import { QUERY_KEYS } from 'components/RabbitInitiativeSidePanel/datahooks';
import { useInitiativeCommentsData } from 'components/RabbitInitiativeSidePanel/datahooks/useCommentsData';
import { HistorySectionHeader } from 'components/RabbitOKRSidePanel/Components/SectionHeaders';
import { OKRProgressGraph } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Overview/OKRProgressGraph';
import { EmptyHistoryUI } from 'components/RabbitOKRSidePanel/SecondaryPanel/FullHistoryPanel/FullHistoryPanel';
import { openFullHistoryPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import useOKRConfig from 'components/hooks/useOKRConfig';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import Post from 'models/post';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { ArrowForward } from 'assets/assetsV1/iconComponents/ArrowForward';

import { OBJECTIVE_TYPES_MAP, OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { findAndReplaceString } from 'constants/helper';
import { sendViewFullHistoryClickedEvent } from 'constants/mixpanelHelper';

import { BUTTON_VARIANT, Button } from 'uiToolkitV1/Button';
import { ListWarning } from 'uiToolkitV1/ListWarning';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextLabel } from 'uiToolkitV1/Typography';

import AutomatedCommentHistory from './AudomatedCommentHistory';
import ManualComment from './ManualComment';

interface Props {
  isOKRPanelOpen: boolean;
  ids: string[];
}

type Comment = {
  id: string | number;
};

type Comments = Comment[];

const OFFSET = 0;
const LIMIT = 3;

const WarningTextComponent = ({ moduleName }) => (
  <Box display="flex" flexDirection="column">
    <TextLabel color="#333" bold>
      You are viewing the {modifyString(moduleName, StringModifier.SENTENCE_CASE)}&apos;s history and details.
    </TextLabel>
    <TextLabel>This is a read-only view.</TextLabel>
    <TextLabel color="#333">
      To edit or manage the {modifyString(moduleName, StringModifier.SENTENCE_CASE)}, please go to &apos;I want to
      manage {modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}’ page.
    </TextLabel>
  </Box>
);

const LoadingCommentUI = ({ isLoading }) => {
  let content = null;
  if (isLoading) {
    content = <SkeletonLoader />;
  }
  return content;
};

const History = ({ isOKRPanelOpen, ids }: Props) => {
  const {
    uuid,
    type,
    startingProgressValue,
    target,
    actual,
    goalClosureStatus,
    goalProgressLevel,
    measure,
    startTS,
    dueTS,
  } = useSelector(kRDrawerSelector);
  const { keyResultsName, objectivesName, moduleName } = useOrganisationSettings();

  const { OBJECTIVE_TYPE } = useOKRConfig();

  const dispatch = useDispatch();

  const isObjective = type === OKR_TYPES.OBJECTIVE;

  const isDetailedObjectiveType = isObjective && OBJECTIVE_TYPE === OBJECTIVE_TYPES_MAP.DETAILED.value;

  /* hiding graph in case goalProgressLevel is null because it breaks graph on ui */
  const showGraphComp = goalProgressLevel && (!isObjective || isDetailedObjectiveType);
  const location = useLocation();
  const isBulkCheckInScreen = location.pathname.includes('goal/check-in/');

  const queryClient = useQueryClient();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  // COMMENTING IT OUT FOR HOTFIX
  // TODO: Uncomment this when BE has figured out the issue with the comments API

  // const { data: commentsData = [], isLoading } = useInitiativeCommentsData({
  //   enabled: isOKRPanelOpen,
  //   id: ids,
  //   offset: OFFSET,
  //   limit: LIMIT,
  // });

  const commentsData = [];
  const isLoading = false;

  const noComments = commentsData?.length === 0;

  const handleCommentUpdate = useCallback(
    ({ commentId, comment }) => {
      const queryKey = [QUERY_KEYS.INITIATIVE_COMMENTS, ids, OFFSET, LIMIT];
      /* update comment data in react query cache */
      queryClient.setQueryData(queryKey, (comments: Comments) =>
        comments.map((_comment) => (_comment.id === commentId ? comment : _comment))
      );
    },
    [ids, queryClient]
  );

  return (
    // @ts-expect-error:  Expression is too complex
    <Flex flexDirection="column" alignItems="stretch" flexWrap="nowrap" w="100%">
      <HistorySectionHeader
        warningTextComp={
          isBulkCheckInScreen ? (
            <ListWarning warningText={<WarningTextComponent moduleName={moduleName} />} alignItems="flex-start" />
          ) : null
        }
      />
      <Divider sx={{ borderColor: '#CFD7DF' }} marginBottom="12px" />
      {showGraphComp ? (
        <OKRProgressGraph
          // @ts-expect-error:  expected params error
          kRId={uuid}
          startValue={startingProgressValue}
          achieved={actual}
          targetValue={target}
          measure={measure}
          startTS={startTS}
          dueTS={dueTS}
          progressLevel={goalProgressLevel}
          goalClosureStatus={goalClosureStatus}
        />
      ) : null}
      {/* {noComments && !isLoading ? (
        <EmptyHistoryUI isObjective={isObjective} objectivesName={objectivesName} keyResultsName={keyResultsName} />
      ) : null} */}
      {/* <Flex
        flexDir="column"
        gap="12px"
        alignItems="flex-start"
        padding="8px"
        maxHeight={noComments ? 'auto' : '200px'}
        overflowY="auto"
      >
        <LoadingCommentUI isLoading={isLoading} />

        {noComments === false &&
          commentsData.map((comment) => {
            const updatedComment = {
              ...comment,
              text: findAndReplaceString(comment?.text, keyResultsName),
            };
            return (
              <Box
                width="100%"
                sx={{
                  '.history-comment-container': {
                    width: '100%',
                  },
                }}
              >
                {' '}
                <CommentContainer
                  placeholder="Share suggestion or feedback"
                  key={`${updatedComment.id}-taskComment`}
                  {...updatedComment}
                  feedId={ids}
                  feedType="TASK"
                  displayReply={false}
                  styleType="default"
                  employeeId={loggedInEmployee.id}
                  className="history-comment-container"
                  renderDisplayCommentView={({ subType, ...props }) =>
                    subType === Post.SUB_TYPES.automatedComment ? (
                      <AutomatedCommentHistory {...props} />
                    ) : (
                      <ManualComment commentMoreMenuItems={props?.commentMoreMenuItems} {...props} />
                    )
                  }
                  updateFeedComment={handleCommentUpdate}
                />
              </Box>
            );
          })}
      </Flex> */}
      {/* {noComments === false && ( */}
      <Box
        display="flex"
        padding="8px"
        paddingTop="12px"
        justifyContent="center"
        alignItems="center"
        gap="8px"
        alignSelf="stretch"
      >
        <Button
          variant={BUTTON_VARIANT.tertiary}
          EndIcon={ArrowForward}
          endIconProps={{ boxSize: '16px' }}
          sx={{ '.chakra-text': { fontSize: '12px', lineHeight: '16px', fontWeight: 600 } }}
          onClick={() => {
            sendViewFullHistoryClickedEvent({
              eventProperties: { goalId: uuid, source: 'view full history button' },
            });
            dispatch(openFullHistoryPanel({ ids }));
          }}
        >
          View full history
        </Button>
      </Box>
      {/* )} */}
    </Flex>
  );
};

export default History;

type RefableHistoryProps = {
  isOKRPanelOpen: boolean;
  ids: string[];
  // ... other props
};

export const RefableHistory = forwardRef((props: RefableHistoryProps, ref) => (
  <Box ref={ref as React.Ref<HTMLDivElement>} w="100%">
    <History isOKRPanelOpen={props?.isOKRPanelOpen} ids={props?.ids} {...props} />
  </Box>
));
